import {
    PLATFORM_TEMPLATES,
    POSTBACK_EVENT_TYPES,
    getMacroAllowedPlatforms,
    isPostbackMacroDisallowedForCell,
    substitutePostbackNetworkName,
} from './consts';

// Per-row postback-template defaults: macro → partner_parameter_key. Optional `eventTypes`
// restricts to specific event types (default: all). Filtered at apply time by macro/platform.
// Lives in this leaf module (separate from platformTemplateDefaults.js) so that callers like
// postbackEventsPlatformsUpdates.js can resolve template entries without creating a circular
// dependency with the cascade module that imports it back.
const HASOFFERS_POSTBACK_TEMPLATE_KEYS = [
    { macro: '{cl?NetworkName}', key: 'transaction_id', required: true },
    {
        macro: '{EVTNAME}',
        key: 'goal_name',
        eventTypes: [POSTBACK_EVENT_TYPES.POST_INSTALL_EVENTS, POSTBACK_EVENT_TYPES.REVENUE_EVENTS],
        required: true,
    },
    { macro: '{AMOUNT}', key: 'amount', eventTypes: [POSTBACK_EVENT_TYPES.REVENUE_EVENTS] },
    { macro: '{pcid?NetworkName}', key: 'adv_id' },
];

const AFFISE_POSTBACK_TEMPLATE_KEYS = [
    { macro: '{cl?NetworkName}', key: 'click_id', required: true },
    {
        macro: '{EVTNAME}',
        key: 'goal',
        eventTypes: [POSTBACK_EVENT_TYPES.POST_INSTALL_EVENTS, POSTBACK_EVENT_TYPES.REVENUE_EVENTS],
        required: true,
    },
    { macro: '{AMOUNT}', key: 'sum', eventTypes: [POSTBACK_EVENT_TYPES.REVENUE_EVENTS] },
    { macro: '{TOUCHPOINT_IP}', key: 'ip' },
    { macro: '{DEVICE_MODEL}', key: 'device_type' },
    { macro: '{FRAUD_REASON_EXTERNAL}', key: 'comment', eventTypes: [POSTBACK_EVENT_TYPES.FRAUD_POSTBACKS] },
];

const EVERFLOW_POSTBACK_TEMPLATE_KEYS = [
    { macro: '{cl?NetworkName}', key: 'transaction_id', required: true },
    {
        macro: '{EVTNAME}',
        key: 'adv_event_id',
        eventTypes: [POSTBACK_EVENT_TYPES.POST_INSTALL_EVENTS, POSTBACK_EVENT_TYPES.REVENUE_EVENTS],
        required: true,
    },
    { macro: '{AMOUNT}', key: 'amount', eventTypes: [POSTBACK_EVENT_TYPES.REVENUE_EVENTS], required: true },
    { macro: '{paffid?NetworkName}', key: 'affiliate_id' },
    { macro: '{paffn?NetworkName}', key: 'affiliate_name' },
    { macro: '{AIFA}', key: 'google_aid' },
    { macro: '{CURRENCY}', key: 'currency', eventTypes: [POSTBACK_EVENT_TYPES.REVENUE_EVENTS] },
    { macro: '{pcrid?NetworkName}', key: 'creative_id' },
    { macro: '{pcrn?NetworkName}', key: 'creative_name' },
    { macro: '{UTC}', key: 'datetime' },
    { macro: '{DEVICE_BRAND}', key: 'device_brand' },
    { macro: '{DEVICE_MODEL}', key: 'device_model' },
    { macro: '{OS_VERSION}', key: 'device_os_version' },
    { macro: '{PLATFORM}', key: 'device_platform' },
    { macro: '{IDFA}', key: 'idfa' },
];

export const POSTBACK_TEMPLATE_KEYS_BY_TEMPLATE = {
    [PLATFORM_TEMPLATES.HASOFFERS]: HASOFFERS_POSTBACK_TEMPLATE_KEYS,
    [PLATFORM_TEMPLATES.AFFISE]: AFFISE_POSTBACK_TEMPLATE_KEYS,
    [PLATFORM_TEMPLATES.EVERFLOW]: EVERFLOW_POSTBACK_TEMPLATE_KEYS,
};

// True if a `postback_template_keys` entry should be applied to this (event_type, platform) cell.
// `templateValue` is optional; when supplied, entries whose macro is disallowed in the
// (template, event_type) pair are also filtered out — independent of the cascade's later
// stripDisallowedMacrosFromTemplates pass, so a malformed template definition can't silently
// seed a forbidden row.
export const entryAppliesToCell = (entry, eventType, platform, templateValue) => {
    if (entry.eventTypes && !entry.eventTypes.includes(eventType)) return false;
    if (!getMacroAllowedPlatforms(entry.macro).includes(platform)) return false;
    if (templateValue && isPostbackMacroDisallowedForCell(templateValue, eventType, entry.macro)) return false;
    return true;
};

// Resolve the list of postback-template entries that apply to a (template, event_type, platform)
// cell. Returns `[{ macro, resolvedMacro, key, required }]`. Used by the macro table UI, the FE
// submit validator, and the new-combo seeding in applyPostbackEventsPlatformsNext — all three
// share one definition of "what entries apply" and stay in sync if filtering rules change.
// Returns `[]` for unknown templates.
export const resolveTemplateEntries = (templateValue, eventType, platform, networkName) => {
    const entries = POSTBACK_TEMPLATE_KEYS_BY_TEMPLATE[templateValue] || [];
    return entries
        .filter(e => entryAppliesToCell(e, eventType, platform, templateValue))
        .map(e => ({
            macro: e.macro,
            resolvedMacro: substitutePostbackNetworkName(e.macro, networkName),
            key: e.key,
            required: !!e.required,
        }));
};
